'use es6';

import { createSelector } from 'reselect';
import { getContentModel, createContentModelSelector } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getIsUngatedForBEValidation } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { getErrorMap } from 'EmailEditorUI/redux/selectors/ApiValidationSelectors';
import { getIsRecipientRequiringEmail, getIsAbTest } from './EmailSelectors';
import { getIsPortalRecipientFatigueSuppressionEnabled } from 'EmailEditorUI/redux/selectors/portalSettingsSelectors';
import { getIsTransactional } from 'EmailEditorUI/redux/selectors/subscriptionSelectors';
import { basicSelector } from 'ContentEditorUI/redux/selectors/helpers';
import { getShouldPublishImmediately } from 'ContentEditorUI/js/redux/selectors/baseContentModelSelectors';
// @ts-expect-error not migrated to ts yet
import { getEmailCreditUsed } from 'EmailEditorUI/redux/selectors/PortalSendCreditsSelectors';
import { getIsMc0653Variation } from 'EmailEditorUI/hooks/experiments/useIsMc0653Variant';
import Raven from 'raven-js';
export const getRecipientsData = basicSelector(state => state.recipientsData);
const getTotalContacts = basicSelector(state => state.recipientsData.get('contacts'));
export const getTotalLists = basicSelector(state => state.recipientsData.get('lists'));
export const getCountLoaded = basicSelector(state => state.recipientsData.get('countInitiallyFetched'));
export const getIsLoadingRecipientsCount = basicSelector(state => state.recipientsData.get('isLoadingCounts'));
export const getListsLoaded = basicSelector(state => state.recipientsData.get('listsInitiallyFetched'));
export const getContactsLoaded = basicSelector(state => state.recipientsData.get('contactsInitiallyFetched'));
export const listCreationInProgress = basicSelector(state => state.recipientsData.get('listCreationInProgress'));
export const getNumToSendTo = basicSelector(state => state.recipientsData.get('numToSendTo'));
export const getActualNumToSendTo = createSelector([getNumToSendTo, getShouldPublishImmediately, getEmailCreditUsed, getIsMc0653Variation], (numToSendTo, publishImmediately, portalCreditsUsed, showMc0653Variant) => {
  const freeEmailSendsLimit = 2000;
  const remainingEmailSends = Math.max(0, freeEmailSendsLimit - portalCreditsUsed);
  const adjustedNumToSendTo = numToSendTo > remainingEmailSends ? remainingEmailSends : numToSendTo;
  const showAdjustedNumToSend = showMc0653Variant && publishImmediately;
  return showAdjustedNumToSend ? adjustedNumToSendTo : numToSendTo;
});
export const getTotalIncluded = basicSelector(state => state.recipientsData.get('totalIncluded'));
export const getNonMarketableContactsNumber = basicSelector(state => state.recipientsData.get('nonMarketable'));
export const getBouncedContactsNumber = basicSelector(state => state.recipientsData.get('bounced'));
export const getNoEmailContactsNumber = basicSelector(state => state.recipientsData.get('noEmail'));
export const getBadEmailContactsNumber = basicSelector(state => state.recipientsData.get('badEmail'));
export const getSendFrequencyCapContactsNumber = basicSelector(state => state.recipientsData.get('recipientFatigued'));
export const getQuarantinedContactsNumber = basicSelector(state => state.recipientsData.get('quarantined'));
export const getGrayMailSuppressionContactsNumber = basicSelector(state => state.recipientsData.get('unengaged'));
export const getExclusionListContactsNumber = basicSelector(state => state.recipientsData.get('exclusionList'));
export const getRemovedForOptimizationContactsNumber = basicSelector(state => state.recipientsData.get('lowContactQualityScore'));
export const getContactQualityScoreSegments = basicSelector(state => state.recipientsData.get('contactQualityScoreSegments'));
export const getTotalDroppedContactsNumber = basicSelector(state => {
  const badEmail = state.recipientsData.get('badEmail') || 0;
  const bounced = state.recipientsData.get('bounced') || 0;
  const grayMailSuppression = state.recipientsData.get('unengaged') || 0;
  const noEmail = state.recipientsData.get('noEmail') || 0;
  const nonMarketable = state.recipientsData.get('nonMarketable') || 0;
  const quarantined = state.recipientsData.get('quarantined') || 0;
  const removedForOptimization = state.recipientsData.get('lowContactQualityScore') || 0;
  const recipientFatigued = state.recipientsData.get('recipientFatigued') || 0;
  const exclusionList = state.recipientsData.get('exclusionList') || 0;
  return badEmail + bounced + grayMailSuppression + noEmail + quarantined + removedForOptimization + recipientFatigued + nonMarketable + exclusionList;
});
export const getContactsEngagementLevel = basicSelector(state => state.recipientsData.get('contactsEngagementLevel'));
export const getShouldSendToUnengagedRecipients = basicSelector(state => !state.recipientsData.get('graymailEnabled'));
export const getIsStaticListLimitReached = basicSelector(state => state.recipientsData.get('staticListLimitReached'));
export const isForcedReloadRecipientsCount = basicSelector(state => state.recipientsData.get('forceRecipientCountReload'));
export const getSendSizePreviewStatus = basicSelector(state => state.recipientsData.get('sendSizePreviewStatus'));

// Use standard ContentEditorUI selector factory for consistency
export const getMailingIlsListsIncluded = createContentModelSelector('mailingIlsListsIncluded');
export const getMailingIlsListsExcluded = createContentModelSelector('mailingIlsListsExcluded');
export const getVidsIncluded = createContentModelSelector('vidsIncluded');
export const getVidsExcluded = createContentModelSelector('vidsExcluded');
export const getIsGraymailSuppressionEnabled = createContentModelSelector('isGraymailSuppressionEnabled');
export const getIsEngagementOptimizationEnabled = createContentModelSelector('isEngagementOptimizationEnabled');
export const getSelectedContactQualityScoreSegment = createContentModelSelector('contactQualityScoreSegment');
export const getContactQualityScoreThreshold = createContentModelSelector('contactQualityScoreThreshold');
export const getIsRecipientFatigueSuppressionEnabled = createContentModelSelector('isRecipientFatigueSuppressionEnabled');
export const getContentModelRecipientsData = createSelector([getMailingIlsListsIncluded, getMailingIlsListsExcluded, getVidsIncluded, getVidsExcluded, getIsGraymailSuppressionEnabled, getIsRecipientFatigueSuppressionEnabled, getIsTransactional, getIsPortalRecipientFatigueSuppressionEnabled, getIsEngagementOptimizationEnabled, getSelectedContactQualityScoreSegment, getContactQualityScoreThreshold], (mailingIlsListsIncluded, mailingIlsListsExcluded, vidsIncluded, vidsExcluded, isGraymailSuppressionEnabled, isRecipientFatigueSuppressionEnabled, transactional, isPortalRecipientFatigueSuppressionEnabled, isEngagementOptimizationEnabled, contactQualityScoreSegment, contactQualityScoreThreshold) => {
  return {
    mailingIlsListsIncluded,
    mailingIlsListsExcluded,
    vidsIncluded,
    vidsExcluded,
    isGraymailSuppressionEnabled: transactional ? false : isGraymailSuppressionEnabled,
    isRecipientFatigueSuppressionEnabled: isPortalRecipientFatigueSuppressionEnabled && isRecipientFatigueSuppressionEnabled !== false,
    isEngagementOptimizationEnabled: transactional ? false : isEngagementOptimizationEnabled,
    contactQualityScoreSegment,
    contactQualityScoreThreshold
  };
});
const getIncludedIds = createSelector([getContentModel, getMailingIlsListsIncluded, getVidsIncluded], (contentModel, mailingIlsListsIncluded, vidsIncluded) => {
  if (!mailingIlsListsIncluded) {
    Raven.captureException(new Error('mailingIlsListsIncluded is undefined'), {
      extra: {
        contentModel
      }
    });
  }
  const listIds = mailingIlsListsIncluded || [];
  return {
    listIds,
    contactIds: vidsIncluded
  };
});
const getExcludedIds = createSelector([getMailingIlsListsExcluded, getVidsExcluded], (mailingIlsListsExcluded, vidsExcluded) => {
  return {
    listIds: mailingIlsListsExcluded,
    contactIds: vidsExcluded
  };
});
const getSelectedListIds = createSelector([getMailingIlsListsIncluded, getMailingIlsListsExcluded], (mailingIlsListsIncluded, mailingIlsListsExcluded) => {
  return {
    included: mailingIlsListsIncluded,
    excluded: mailingIlsListsExcluded
  };
});
const getSelectedContactIds = createSelector([getVidsIncluded, getVidsExcluded], (vidsIncluded, vidsExcluded) => {
  return {
    included: vidsIncluded,
    excluded: vidsExcluded
  };
});
export const getSelectedLists = createSelector([getSelectedListIds, getTotalLists], ({
  included,
  excluded
}, lists) => {
  return {
    includedLists: lists.filter(list => included.indexOf(list.get('ilsListId')) > -1),
    excludedLists: lists.filter(list => excluded.indexOf(list.get('ilsListId')) > -1)
  };
});
export const getPreviewContacts = createSelector([getTotalContacts], contacts => contacts.map(contact => contact.getAsSelectOption()));
export const getIncludedRecipients = createSelector([getIncludedIds, getTotalContacts, getTotalLists], (ids, contacts, lists) => {
  const {
    listIds,
    contactIds
  } = ids;
  return {
    includedContacts: contacts.filter(contact => contactIds.indexOf(contact.get('vid')) > -1),
    includedLists: lists.filter(list => listIds.indexOf(list.get('ilsListId')) > -1)
  };
});
export const getSingleRecipient = createSelector([getIncludedRecipients], recipients => {
  if (!recipients.includedLists.isEmpty() || recipients.includedContacts.size !== 1) {
    return null;
  }
  return recipients.includedContacts.first();
});
export const getHasSelectedRecipients = createSelector([getIncludedIds, getExcludedIds], (included, excluded) => {
  return included.listIds.length > 0 || included.contactIds.length > 0 || excluded.listIds.length > 0 || included.contactIds.length > 0;
});
export const getDeletedListIds = createSelector([getSelectedListIds, getTotalLists], ({
  included,
  excluded
}, lists) => {
  const allSelected = [...included, ...excluded];
  return allSelected.filter(ilsListId => {
    const list = lists.get(ilsListId);
    return list && list.get('deleted');
  });
});
export const getDeletedContactIds = createSelector([getSelectedContactIds, getTotalContacts], ({
  included,
  excluded
}, contacts) => {
  const allSelected = [...included, ...excluded];
  return allSelected.filter(id => {
    const contact = contacts.get(id);
    return contact && contact.get('deleted');
  });
});
export const getHasDeletedSelectedRecipients = createSelector([getDeletedContactIds, getDeletedListIds], (deletedContacts, deletedLists) => {
  return deletedContacts.length > 0 || deletedLists.length > 0;
});
export const getHasNoLists = createSelector([getTotalLists, getListsLoaded], (lists, loaded) => lists.isEmpty() && loaded);
export const getHasNoContacts = createSelector([getTotalContacts, getContactsLoaded], (contacts, loaded) => contacts.isEmpty() && loaded);
const getSearchLists = basicSelector(state => state.recipientsData.get('listsSearchResults'));
export const getSortedSearchLists = createSelector([getSearchLists], lists => {
  return lists.toList().sort((a, b) => b.get('createdAt') - a.get('createdAt')).toArray();
});
const getSearchContacts = basicSelector(state => state.recipientsData.get('contactsSearchResults'));
export const getSortedSearchContacts = createSelector([getSearchContacts], contacts => {
  return contacts.toList().sort((a, b) => b.get('addedAt') - a.get('addedAt')).toArray();
});
export const isRecipientsValid = createSelector([getIncludedIds, getIsAbTest, getIsRecipientRequiringEmail, getHasDeletedSelectedRecipients], ({
  listIds,
  contactIds
}, isAbTest, isRecipientRequiringEmail, hasDeletedRecipients) => {
  if (!isRecipientRequiringEmail) {
    return true;
  }
  if (hasDeletedRecipients) {
    return false;
  }
  if (isAbTest) {
    return listIds.length > 0;
  }
  return listIds.length > 0 || contactIds.length > 0;
});
export const getRecipientsValidationErrors = createSelector([getIncludedIds, getIsAbTest, getIsRecipientRequiringEmail, getIsUngatedForBEValidation, getErrorMap], ({
  listIds,
  contactIds
}, isAbTest, isRecipientRequiringEmail, isUngatedForBEValidation, errorMap) => {
  if (isUngatedForBEValidation) {
    if (errorMap['NO_RECIPIENTS_INCLUDED']) {
      return {
        mailing_ils_lists_included: {
          pane: 'recipients',
          type: 'required',
          translated: true
        }
      };
    }
    return {};
  } else {
    if (!isRecipientRequiringEmail) {
      return {};
    }
    if (listIds.length === 0) {
      if (isAbTest || contactIds.length === 0) {
        return {
          mailing_ils_lists_included: {
            pane: 'recipients',
            type: 'required',
            translated: true
          }
        };
      }
    }
    return {};
  }
});
export const getExcludedRecipients = createSelector([getExcludedIds, getTotalContacts, getTotalLists], (ids, contacts, lists) => {
  const {
    listIds,
    contactIds
  } = ids;
  return {
    excludedContacts: contacts.filter(contact => contactIds.indexOf(contact.get('vid')) > -1),
    excludedLists: lists.filter(list => listIds.indexOf(list.get('ilsListId')) > -1)
  };
});
export const getContactQualityScoreThresholdForSelectedSegment = createSelector([getSelectedContactQualityScoreSegment, getContactQualityScoreSegments], (contactQualityScoreSegment, contactQualityScoreSegments) => {
  if (!contactQualityScoreSegment) {
    return null;
  }
  const segment = contactQualityScoreSegments.find(cqs => cqs.segmentDescription === contactQualityScoreSegment);
  return segment === null || segment === void 0 ? void 0 : segment.percentileForExclusion;
});
export const getPredictedOpenRatePercentage = createSelector([getSelectedContactQualityScoreSegment, getContactQualityScoreSegments], (contactQualityScoreSegment, contactQualityScoreSegments) => {
  if (!contactQualityScoreSegment) {
    return null;
  }
  const segment = contactQualityScoreSegments.find(cqs => cqs.segmentDescription === contactQualityScoreSegment);
  if (!segment || !segment.predictedEmailOpenRate) {
    return null;
  }
  return `${Math.round(segment.predictedEmailOpenRate * 100)}`;
});